import { supabase } from './supabase';

/**
 * Tag Category interface
 */
export interface TagCategory {
  id: number;
  name: string;
  description: string | null;
  color: string;
  project_id: number;
  created_at: string;
  updated_at: string;
  tag_count?: number;
}

/**
 * Get all tag categories for a project
 * @param projectId - Project ID
 * @returns Promise with tag categories array
 */
export const getTagCategories = async (projectId: string | number): Promise<TagCategory[]> => {
  try {
    const parsedProjectId = typeof projectId === 'string' ? parseInt(projectId) : projectId;
    
    // Direct query to get tag categories with tag count
    const { data, error } = await supabase
      .from('tag_categories')
      .select(`
        id,
        name,
        description,
        color,
        project_id,
        created_at,
        updated_at,
        tags(count)
      `)
      .eq('project_id', parsedProjectId)
      .order('name');

    if (error) {
      console.error('Error fetching tag categories:', error);
      throw error;
    }

    // Transform the data to include tag_count
    const transformedData = data.map(category => ({
      ...category,
      tag_count: category.tags[0]?.count || 0,
      tags: undefined // Remove the nested array
    }));

    return transformedData || [];
  } catch (error) {
    console.error('Error in getTagCategories:', error);
    throw error;
  }
};

/**
 * Create a new tag category
 * @param category - Tag category data
 * @returns Promise with created tag category
 */
export const createTagCategory = async (
  category: {
    name: string;
    description?: string;
    color?: string;
    project_id: number;
  }
): Promise<TagCategory> => {
  try {
    // Insert the tag category - tenant_id will be set by the database trigger
    const { data, error } = await supabase
      .from('tag_categories')
      .insert([
        {
          name: category.name,
          description: category.description || null,
          color: category.color || '#94a3b8',
          project_id: category.project_id
        }
      ])
      .select()
      .single();

    if (error) {
      console.error('Error creating tag category:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in createTagCategory:', error);
    throw error;
  }
};

/**
 * Update a tag category
 * @param categoryId - Tag category ID
 * @param updates - Tag category updates
 * @returns Promise with updated tag category
 */
export const updateTagCategory = async (
  categoryId: number,
  updates: {
    name?: string;
    description?: string | null;
    color?: string;
  }
): Promise<TagCategory> => {
  try {
    const { data, error } = await supabase
      .from('tag_categories')
      .update(updates)
      .eq('id', categoryId)
      .select()
      .single();

    if (error) {
      console.error('Error updating tag category:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in updateTagCategory:', error);
    throw error;
  }
};

/**
 * Delete a tag category
 * @param categoryId - Tag category ID
 * @returns Promise with success status
 */
export const deleteTagCategory = async (categoryId: number): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('tag_categories')
      .delete()
      .eq('id', categoryId);

    if (error) {
      console.error('Error deleting tag category:', error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteTagCategory:', error);
    throw error;
  }
};
